import { useRef, useEffect, useState, useCallback } from "react";
import { ArrowUpRight, MessageCircle, Calculator, LayoutDashboard, Camera, Layers, Lock, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { NAA_BEAT_ASSETS, PROJECTS } from "@/lib/siteConfig";

const NAA = PROJECTS.find((p) => p.id === "naautomobile");
const RED = "#e11d48"; // ruhiges Premium-Rot — CloudService bleibt dominant
const WA_GREEN = "#25d366"; // echtes WhatsApp-Brand-Gruen, sparsam eingesetzt

// Lazy autoplay video: loads + plays only when visible, pauses offscreen.
const LazyVideo = ({ src, webm, poster, width, height, testId }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (v.preload === "none") v.preload = "auto";
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      data-testid={testId}
      poster={poster}
      width={width}
      height={height}
      muted
      loop
      playsInline
      preload="none"
      className="block w-full h-auto"
    >
      <source src={src} type="video/mp4" />
      {webm && <source src={webm} type="video/webm" />}
    </video>
  );
};

const Kicker = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
    {Icon ? (
      <Icon className="w-3.5 h-3.5" style={{ color: RED }} />
    ) : (
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RED }} aria-hidden="true" />
    )}
    {children}
  </p>
);

const Shot = ({ asset, eager = false, className = "" }) => (
  <img
    src={asset.src}
    alt={asset.alt}
    width={asset.w}
    height={asset.h}
    loading={eager ? "eager" : "lazy"}
    className={`block w-full h-auto ${className}`}
  />
);

/* ============ Interaktiver Before/After-Slider (View-Lock Proof) ============ */
const BeforeAfterSlider = ({ before, after, labelBefore, labelAfter, hint }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(97, Math.max(3, p)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const endDrag = () => {
    dragging.current = false;
  };

  return (
    <div className="select-none">
      <div
        ref={ref}
        data-testid="naa-before-after-slider"
        className="relative overflow-hidden rounded-xl border border-white/12 cursor-ew-resize"
        style={{ aspectRatio: `${after.w}/${after.h}`, touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        role="slider"
        aria-label={`${labelBefore} / ${labelAfter}`}
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setPos((p) => Math.max(3, p - 4));
          if (e.key === "ArrowRight") setPos((p) => Math.min(97, p + 4));
        }}
      >
        {/* After (full background) */}
        <img
          src={after.src}
          alt={after.alt}
          width={after.w}
          height={after.h}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="naa-ba-after-img"
        />
        {/* Before (clipped to slider position) */}
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img
            src={before.src}
            alt={before.alt}
            width={before.w}
            height={before.h}
            loading="lazy"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="naa-ba-before-img"
          />
        </div>
        {/* Divider + handle */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white/85"
          style={{ left: `${pos}%`, transform: "translateX(-1px)", boxShadow: "0 0 12px rgba(255,255,255,0.45)" }}
          aria-hidden="true"
        />
        <div
          data-testid="naa-ba-handle"
          className="absolute top-1/2 w-10 h-10 rounded-full border border-white/30 bg-[#0b0f1a]/80 backdrop-blur-md flex items-center justify-center"
          style={{ left: `${pos}%`, transform: "translate(-50%, -50%)", touchAction: "none" }}
          aria-hidden="true"
        >
          <span className="text-white/85 text-xs font-mono-cs tracking-tight">&#8596;</span>
        </div>
        {/* Labels (bottom — die Produkt-UI hat eigene Chips oben links) */}
        <span
          data-testid="naa-ba-label-before"
          className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-mono-cs uppercase tracking-[0.14em] bg-black/55 backdrop-blur-md border border-white/15 text-white/90"
        >
          {labelBefore}
        </span>
        <span
          data-testid="naa-ba-label-after"
          className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-mono-cs uppercase tracking-[0.14em] backdrop-blur-md border text-white"
          style={{ backgroundColor: "rgba(225,29,72,0.28)", borderColor: "rgba(225,29,72,0.45)" }}
        >
          {labelAfter}
        </span>
      </div>
      <p className="mt-3 text-center text-xs font-mono-cs uppercase tracking-[0.16em] text-muted-foreground/70">{hint}</p>
    </div>
  );
};

/* ============ Hauptsektion ============ */
export const NAAutomobileCaseStudy = () => {
  const { t } = useLang();

  return (
    <section
      id="case-naautomobile"
      data-testid="case-study-naautomobile"
      className="relative py-20 sm:py-28 bg-[#070a12] border-y border-white/5 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute top-[2%] right-[-12%] w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(225,29,72,0.07), transparent 70%)" }}
      />

      {/* ---- Header ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Kicker>{t.naa.kicker}</Kicker>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.025em] leading-[1.08] text-foreground max-w-3xl">
            {t.naa.headline}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl">{t.naa.intro}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {NAA.allBadges.map((b) => (
              <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
                {b}
              </Badge>
            ))}
          </div>
          <a
            data-testid="naa-visit-link"
            href={NAA.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
          >
            {t.naa.visit} <ArrowUpRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>

      {/* ---- Beat 1 — Premium Digital Showroom ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-16" data-testid="naa-beat-1">
        <Reveal>
          <Kicker>{t.naa.b1Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b1Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.naa.b1Text}</p>
        </Reveal>
        <Reveal delay={0.12} className="mt-9">
          <DeviceFrameDesktop url="naautomobile.at" testId="naa-hero-frame">
            <Shot asset={NAA_BEAT_ASSETS.heroDesktop} eager />
          </DeviceFrameDesktop>
        </Reveal>
      </div>

      {/* ---- Beat 2 — Fahrzeugbestand & Detailseiten ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="naa-beat-2">
        <Reveal>
          <Kicker>{t.naa.b2Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b2Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.naa.b2Text}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-9">
          <DeviceFrameDesktop url="naautomobile.at/fahrzeuge" testId="naa-inventory-frame">
            <Shot asset={NAA_BEAT_ASSETS.inventoryDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
        <Reveal delay={0.12} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <DeviceFrameDesktop url="naautomobile.at" testId="naa-detail-frame">
              <Shot asset={NAA_BEAT_ASSETS.detailDesktop} />
            </DeviceFrameDesktop>
          </div>
          <p className="lg:col-span-4 text-sm text-muted-foreground leading-relaxed font-mono-cs">{t.naa.b2Text}</p>
        </Reveal>
      </div>

      {/* ---- Beat 3 — WhatsApp-first Verkauf ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="naa-beat-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
              <MessageCircle className="w-3.5 h-3.5" style={{ color: WA_GREEN }} />
              {t.naa.b3Kicker}
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b3Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.naa.b3Text}</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7 flex justify-center">
            <div className="relative w-[250px] sm:w-[290px]">
              <div
                aria-hidden="true"
                className="absolute -inset-12 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(37,211,102,0.08), transparent 70%)" }}
              />
              <DeviceFramePhone testId="naa-whatsapp-phone">
                <Shot asset={NAA_BEAT_ASSETS.whatsappMobile} />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 4 — Finanzierungsrechner ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="naa-beat-4">
        <Reveal>
          <Kicker icon={Calculator}>{t.naa.b4Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b4Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.naa.b4Text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="naautomobile.at/finanzierung" testId="naa-financing-frame">
              <Shot asset={NAA_BEAT_ASSETS.financingDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="naa-financing-phone">
                <Shot asset={NAA_BEAT_ASSETS.financingMobile} />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 text-xs font-mono-cs text-muted-foreground/60 leading-relaxed max-w-xl" data-testid="naa-financing-disclaimer">
            {t.naa.b4Note}
          </p>
        </Reveal>
      </div>

      {/* ---- Beat 5 — Auto verkaufen / KI-Ankaufseinschaetzung ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="naa-beat-5">
        <Reveal>
          <Kicker>{t.naa.b5Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b5Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.naa.b5Text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="naautomobile.at/verkaufen" testId="naa-sellform-frame">
              <Shot asset={NAA_BEAT_ASSETS.sellFormDesktop} />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="naa-valuation-phone">
                <Shot asset={NAA_BEAT_ASSETS.valuationMobile} />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 order-2 lg:order-1">
            <DeviceFrameDesktop url="naautomobile.at/verkaufen" testId="naa-valuation-frame">
              <Shot asset={NAA_BEAT_ASSETS.valuationDesktop} />
            </DeviceFrameDesktop>
          </div>
          <p className="lg:col-span-4 order-1 lg:order-2 text-xs font-mono-cs text-muted-foreground/60 leading-relaxed" data-testid="naa-valuation-disclaimer">
            {t.naa.b5Note}
          </p>
        </Reveal>
      </div>

      {/* ---- Beat 6 — Haendler-Dashboard (echte Screens) ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="naa-beat-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <Kicker icon={LayoutDashboard}>{t.naa.b6Kicker}</Kicker>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b6Title}</h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{t.naa.b6Text}</p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="flex justify-center gap-6 sm:gap-10">
              <div className="w-[200px] sm:w-[240px] mt-10">
                <DeviceFramePhone testId="naa-dashboard-phone-1">
                  <Shot asset={NAA_BEAT_ASSETS.dashboardMobile} />
                </DeviceFramePhone>
              </div>
              <div className="w-[200px] sm:w-[240px]">
                <DeviceFramePhone testId="naa-dashboard-phone-2">
                  <Shot asset={NAA_BEAT_ASSETS.dashboardMobile2} />
                </DeviceFramePhone>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-12">
          <DeviceFrameDesktop url="naautomobile.at/admin" testId="naa-dashboard-frame">
            <Shot asset={NAA_BEAT_ASSETS.dashboardDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
      </div>

      {/* ---- Beat 7 — AI Photo Studio mit View-Lock (Hero-Moment) ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-28" data-testid="naa-beat-7">
        <Reveal>
          <Kicker icon={Camera}>{t.naa.b7Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b7Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">{t.naa.b7Text}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal delay={0.08} className="lg:col-span-7">
            <BeforeAfterSlider
              before={NAA_BEAT_ASSETS.baStudioBefore}
              after={NAA_BEAT_ASSETS.baStudioAfter}
              labelBefore={t.naa.baLabelBefore}
              labelAfter={t.naa.baLabelAfter}
              hint={t.naa.b7SliderHint}
            />
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-5">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="naa-viewlock-box">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Lock className="w-4 h-4" style={{ color: RED }} />
                {t.naa.viewLockTitle}
              </p>
              <ul className="mt-4 space-y-2.5">
                {t.naa.viewLockPoints.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#00d4ff]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-5 text-xs font-mono-cs text-muted-foreground/60 leading-relaxed" data-testid="naa-studio-disclaimer">
              {t.naa.b7Note}
            </p>
          </Reveal>
        </div>
      </div>

      {/* ---- Beat 8 — AI Background Manager ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-24" data-testid="naa-beat-8">
        <Reveal>
          <Kicker icon={Layers}>{t.naa.b8Kicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.b8Title}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.naa.b8Text}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-9">
          <DeviceFrameDesktop url="naautomobile.at/admin" testId="naa-bgmanager-frame">
            <Shot asset={NAA_BEAT_ASSETS.studioConfigDesktop} />
          </DeviceFrameDesktop>
        </Reveal>
        {/* Zweite Hintergrund-Variante (Showroom) als statisches Paar */}
        <Reveal delay={0.12} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <figure>
            <div className="overflow-hidden rounded-xl border border-white/12">
              <Shot asset={NAA_BEAT_ASSETS.baShowroomBefore} />
            </div>
            <figcaption className="mt-2.5 text-xs font-mono-cs uppercase tracking-[0.16em] text-muted-foreground/70">
              {t.naa.baLabelBefore}
            </figcaption>
          </figure>
          <figure>
            <div className="overflow-hidden rounded-xl border" style={{ borderColor: "rgba(225,29,72,0.35)" }}>
              <Shot asset={NAA_BEAT_ASSETS.baShowroomAfter} />
            </div>
            <figcaption className="mt-2.5 text-xs font-mono-cs uppercase tracking-[0.16em]" style={{ color: RED }}>
              {t.naa.baLabelAfter} — Showroom
            </figcaption>
          </figure>
        </Reveal>
      </div>

      {/* ---- Live Flow ---- */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-28" data-testid="naa-beat-flow">
        <Reveal>
          <Kicker>{t.naa.flowKicker}</Kicker>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">{t.naa.flowTitle}</h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-xl">{t.naa.flowText}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal delay={0.08} className="lg:col-span-8">
            <DeviceFrameDesktop url="naautomobile.at" testId="naa-flow-desktop-frame">
              <LazyVideo
                src={NAA_BEAT_ASSETS.flowDesktop.src}
                webm={NAA_BEAT_ASSETS.flowDesktop.webm}
                poster={NAA_BEAT_ASSETS.flowDesktop.poster}
                width={NAA_BEAT_ASSETS.flowDesktop.w}
                height={NAA_BEAT_ASSETS.flowDesktop.h}
                testId="naa-flow-desktop-video"
              />
            </DeviceFrameDesktop>
          </Reveal>
          <Reveal delay={0.16} className="lg:col-span-4 flex justify-center">
            <div className="w-[230px] sm:w-[260px]">
              <DeviceFramePhone testId="naa-flow-phone-frame">
                <LazyVideo
                  src={NAA_BEAT_ASSETS.flowMobile.src}
                  webm={NAA_BEAT_ASSETS.flowMobile.webm}
                  poster={NAA_BEAT_ASSETS.flowMobile.poster}
                  width={NAA_BEAT_ASSETS.flowMobile.w}
                  height={NAA_BEAT_ASSETS.flowMobile.h}
                  testId="naa-flow-mobile-video"
                />
              </DeviceFramePhone>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
